import { forwardRef } from 'react';
import styled from '@emotion/styled';
import {
  tokens,
  Checkbox,
  Text,
  IconButton,
  Loader,
  Tooltip,
  type CheckboxProps,
} from '@hotjar/hotsauce';

interface BaseOptionCardProps extends Omit<CheckboxProps, 'isIndeterminate'> {
  /**
   * Clear, concise copy that represents the option.
   */
  title: string;
  /**
   * An info icon to be added next to the title.
   */
  hasInfoIcon?: boolean;
  /**
   * A click handler for the info icon.
   */
  onInfoIconClick?: () => void;
  /**
   * The tooltip text for the info icon.
   */
  infoIconTooltipText?: string;
  /**
   * A loading indicator will be displayed instead of the title if true.
   */
  isLoading?: boolean;
  /**
   * Tooltip text to be displayed when an option is disable.
   */
  disabledTooltipText?: string;
}

interface OptionCardWithIllustrationProps extends BaseOptionCardProps {
  /**
   * OptionCard illustration - When provided the respective img would be rendered.
   */
  illustration: string;
}

export type OptionCardProps = OptionCardWithIllustrationProps;

const CardContainer = styled('div')`
  ${tokens.flexCenter()};
  flex-direction: column;
  width: 100%;
  position: relative;
  align-self: start;
`;

const Card = styled.label<{ checked: boolean; isDisabled: boolean }>`
  ${tokens.flexCenter()};
  flex-direction: column;
  padding: ${tokens.spacer.x0pt5};
  margin-bottom: 0;
  border-radius: ${tokens.radius.l};
  border: 1px solid ${tokens.color.ui_border};
  width: 100%;
  position: relative;
  text-align: center;
  justify-content: flex-start;
  text-transform: initial;
  color: ${tokens.color.text_default};
  cursor: pointer;

  &:hover {
    border: 1px solid ${tokens.color.ui_border_alt};
    ${tokens.elevation(1)};
  }

  ${({ checked }) =>
    checked &&
    `
    border-color: ${tokens.color.ui_border_active};
    background-color: ${tokens.color.bg_active};

    &:hover {
      background-color: ${tokens.color.bg_active};
      border-color: ${tokens.color.ui_border_active};
      ${tokens.elevation(1)};
    }
`}

  ${({ isDisabled }) =>
    isDisabled &&
    `
    cursor: not-allowed;
    color: ${tokens.color.text_disabled};
    background-color: ${tokens.color.ui_background};
    border-color: ${tokens.color.ui_disabled};

    &:hover {
      color: ${tokens.color.text_disabled};  
      background-color: ${tokens.color.ui_background};
      border-color: ${tokens.color.ui_disabled};
      ${tokens.elevation(0)};
    }
  `}
`;

const StyledCheckbox = styled(Checkbox)`
  display: none;
`;

const Title = styled(Text)<{ checked: boolean }>`
  margin-top: ${tokens.spacer.x1};
  text-align: center;
  width: 80%;

  ${({ checked }) => checked && `color: ${tokens.color.ui_border_active}`}
`;

const Illustration = styled.span`
  width: 100px;
  height: 68px;
  background-size: contain;
  background-repeat: no-repeat;
  background-blend-mode: multiply;
  background-position: center;
  background-color: inherit;
`;

const StyledIconButton = styled(IconButton)`
  & {
    margin-left: ${tokens.spacer.x0pt5};

    button,
    svg {
      width: ${tokens.spacer.x1pt5};
      height: ${tokens.spacer.x1pt5};
      min-width: ${tokens.spacer.x1pt5};
      min-height: ${tokens.spacer.x1pt5};
    }
  }
`;

const StyledLoader = styled(Loader)`
  margin-top: ${tokens.spacer.x2};
`;

export const OptionCard = forwardRef<HTMLInputElement, OptionCardProps>(
  (
    {
      title,
      illustration,
      className,
      isChecked,
      isDisabled,
      hasInfoIcon,
      onInfoIconClick,
      infoIconTooltipText,
      isLoading,
      disabledTooltipText,
      ...props
    }: OptionCardProps,
    ref,
  ) => {
    return (
      <CardContainer>
        <Tooltip text={disabledTooltipText ?? ''} isDisabled={!(isDisabled ?? false)}>
          <Card
            data-testid="option-card"
            checked={isChecked ?? false}
            isDisabled={isDisabled ?? false}
            className={className}
            role="button"
          >
            <Illustration style={{ backgroundImage: `url('${illustration}')` }} />
            <StyledCheckbox isChecked={isChecked} isDisabled={isDisabled} ref={ref} {...props} />
          </Card>
        </Tooltip>
        {isLoading ?? false ? (
          <StyledLoader size="l" />
        ) : (
          <Title
            textColor={isDisabled ?? false ? 'text_disabled' : 'text_default'}
            textType="body2"
            as="span"
            checked={isChecked ?? false}
          >
            {title}
            {hasInfoIcon ?? false ? (
              <StyledIconButton
                icon="info"
                tooltipText={infoIconTooltipText !== undefined ? infoIconTooltipText : 'Learn more'}
                kind="ghost"
                size="s"
                onClick={() => onInfoIconClick && onInfoIconClick()}
              />
            ) : null}
          </Title>
        )}
      </CardContainer>
    );
  },
);

OptionCard.displayName = 'OptionCard';
